// utils/monitoring.ts
import { BrowserClient, Scope, createTransport, defaultStackParser } from "@sentry/browser";
function scrubMonitoringText(value) {
  return value.slice(0, 2e3).replace(/https?:\/\/[^\s"'<>]+/gi, "[URL]").replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, "[TOKEN]").replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "[EMAIL]").replace(/((?:password|authorization|cookie|secret|token|api[_-]?key)["']?\s*[=:]\s*)(?:"[^"]*"|'[^']*'|[^\s,;}]+)/gi, "$1[FILTERED]");
}
function safeFrameFilename(value) {
  if (!value) return void 0;
  try {
    const url = new URL(value);
    if (!["http:", "https:", "file:", "chrome-extension:"].includes(url.protocol)) return "[script]";
    url.username = "";
    url.password = "";
    url.search = "";
    url.hash = "";
    return url.href.slice(0, 500);
  } catch {
    return value.split(/[?#]/)[0].slice(0, 500);
  }
}
function sanitizeMonitoringEvent(event) {
  const safe = {
    event_id: event.event_id,
    timestamp: event.timestamp,
    platform: event.platform,
    release: event.release,
    environment: event.environment,
    sdk: event.sdk,
    level: event.level,
    message: event.message ? scrubMonitoringText(event.message) : void 0,
    tags: { service: event.tags?.service },
    exception: event.exception ? { values: event.exception.values?.slice(0, 3).map((value) => ({
      type: value.type,
      value: value.value ? scrubMonitoringText(value.value) : void 0,
      stacktrace: value.stacktrace ? { frames: value.stacktrace.frames?.slice(-30).map((frame) => ({
        filename: safeFrameFilename(frame.filename),
        function: frame.function,
        lineno: frame.lineno,
        colno: frame.colno,
        in_app: frame.in_app
      })) } : void 0
    })) } : void 0
  };
  return safe;
}
function createBrowserMonitor(config) {
  let scope;
  let client;
  const seen = /* @__PURE__ */ new WeakSet();
  let count = 0;
  let windowStarted = Date.now();
  try {
    if (config.dsn) {
      const url = new URL(config.dsn);
      if (url.protocol !== "https:" || !url.username || !/^\/\d+$/.test(url.pathname)) throw Error("Invalid DSN");
      client = new BrowserClient({
        dsn: config.dsn,
        environment: config.environment || "production",
        release: config.release,
        integrations: [],
        stackParser: defaultStackParser,
        sendDefaultPii: false,
        sendClientReports: false,
        maxBreadcrumbs: 0,
        beforeSend: sanitizeMonitoringEvent,
        transport: (options) => createTransport({ ...options, bufferSize: 10 }, async (request) => {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 2e3);
          try {
            const response = await fetch(options.url, {
              method: "POST",
              body: request.body,
              credentials: "omit",
              referrerPolicy: "no-referrer",
              redirect: "error",
              signal: controller.signal,
              headers: { "Content-Type": "application/x-sentry-envelope" }
            });
            await response.body?.cancel();
            return { statusCode: response.status, headers: {
              "retry-after": response.headers.get("retry-after"),
              "x-sentry-rate-limits": response.headers.get("x-sentry-rate-limits")
            } };
          } finally {
            clearTimeout(timeout);
          }
        })
      });
      scope = new Scope();
      scope.setClient(client);
      scope.setTag("service", config.service);
      client.init();
    }
  } catch {
    scope = void 0;
    client = void 0;
  }
  return {
    enabled: Boolean(scope),
    capture(error) {
      try {
        if (!scope || !(error instanceof Error)) return;
        if (Date.now() - windowStarted >= 6e4) {
          windowStarted = Date.now();
          count = 0;
        }
        if (seen.has(error) || count >= 10) return;
        seen.add(error);
        count += 1;
        scope.captureException(error);
      } catch {
      }
    },
    flush: () => client?.flush(2e3) ?? Promise.resolve(true)
  };
}
function isOwnedScript(source, origin) {
  try {
    const actual = new URL(source);
    const expected = new URL(origin);
    return actual.protocol === expected.protocol && actual.host === expected.host;
  } catch {
    return false;
  }
}
function listenForOwnedErrors(monitor, target, origin) {
  if (!monitor.enabled) return () => {
  };
  const error = (event) => {
    try {
      if (isOwnedScript(event.filename, origin)) monitor.capture(event.error);
    } catch {
    }
  };
  const rejection = (event) => {
    if (event.reason instanceof Error && event.reason.stack?.match(/(?:https?|chrome-extension):\/\/[^\s)]+/g)?.some((source) => isOwnedScript(source, origin))) monitor.capture(event.reason);
  };
  target.addEventListener("error", error);
  target.addEventListener("unhandledrejection", rejection);
  return () => {
    target.removeEventListener("error", error);
    target.removeEventListener("unhandledrejection", rejection);
  };
}
export {
  createBrowserMonitor,
  isOwnedScript,
  listenForOwnedErrors,
  sanitizeMonitoringEvent,
  scrubMonitoringText
};
//# sourceMappingURL=monitoring.mjs.map